import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from './AuthContext';

const AdminAccessContext = createContext(null);

export const AdminAccessProvider = ({ children }) => {
  const { authenticatedUser, authLoading } = useAuthContext();
  const [state, setState] = useState({
    loading: false,
    loaded: false,
    isAdmin: false,
    access: null,
    error: '',
  });

  useEffect(() => {
    let isMounted = true;

    if (authLoading) {
      setState((current) => ({
        ...current,
        loading: Boolean(authenticatedUser),
        loaded: false,
        error: '',
      }));
      return undefined;
    }

    if (!authenticatedUser?.token) {
      setState({
        loading: false,
        loaded: true,
        isAdmin: false,
        access: null,
        error: '',
      });
      return undefined;
    }

    const controller = new AbortController();

    const loadAdminAccess = async () => {
      setState({
        loading: true,
        loaded: false,
        isAdmin: false,
        access: null,
        error: '',
      });

      try {
        const response = await fetch('/.netlify/functions/admin-access', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authenticatedUser.token}`,
          },
          signal: controller.signal,
        });

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(payload?.message || 'Unable to load admin access.');
        }

        if (!isMounted) {
          return;
        }

        setState({
          loading: false,
          loaded: true,
          isAdmin: Boolean(payload?.access?.isAdmin),
          access: payload?.access || null,
          error: '',
        });
      } catch (error) {
        if (controller.signal.aborted || !isMounted) {
          return;
        }

        setState({
          loading: false,
          loaded: true,
          isAdmin: false,
          access: null,
          error: error.message || 'Unable to load admin access.',
        });
      }
    };

    loadAdminAccess();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [authenticatedUser, authLoading]);

  const value = useMemo(() => ({
    adminAccess: state.access,
    adminError: state.error,
    adminLoaded: state.loaded,
    adminLoading: state.loading,
    isAdmin: state.isAdmin,
  }), [state]);

  return (
    <AdminAccessContext.Provider value={value}>
      {children}
    </AdminAccessContext.Provider>
  );
};

export const useAdminAccess = () => {
  const context = useContext(AdminAccessContext);

  if (!context) {
    throw new Error('useAdminAccess must be used within an AdminAccessProvider');
  }

  return context;
};
